import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Users, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import AuthorEditor from './AuthorEditor';
import blogApiService from '../../services/blogApiService';
import type { BlogAuthor } from '../../types/blog';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatRelativeTime } from '../../utils/helpers';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';

const AuthorList: React.FC = () => {
  const [authors, setAuthors] = useState<BlogAuthor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<BlogAuthor | null>(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAuthor, setDeletingAuthor] = useState<BlogAuthor | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAuthors = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await blogApiService.getAuthors();
      setAuthors(data);
    } catch (error) {
      toast.error(extractErrorMessage(error));
      logger.error('Failed to fetch authors:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  const handleDeleteConfirm = async () => {
    if (!deletingAuthor) return;
    try {
      setIsDeleting(true);
      await blogApiService.deleteAuthor(deletingAuthor.id);
      toast.success('Author deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingAuthor(null);
      fetchAuthors();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditorSave = () => {
    setEditorOpen(false);
    setEditingAuthor(null);
    fetchAuthors();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {authors.length} author{authors.length !== 1 ? 's' : ''}
        </p>
        <Button
          onClick={() => { setEditingAuthor(null); setEditorOpen(true); }}
          className="bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 text-white dark:text-slate-900 shadow-md"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          New Author
        </Button>
      </div>

      {/* Authors grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Loading authors...</p>
          </div>
        </div>
      ) : authors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <Users className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">No authors yet</h3>
          <p className="text-sm text-slate-500 mt-1">Add your first blog author to get started.</p>
          <Button
            onClick={() => { setEditingAuthor(null); setEditorOpen(true); }}
            className="mt-4"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add Author
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {authors.map((author) => (
            <Card
              key={author.id}
              className="group relative border-slate-200 dark:border-slate-800 hover:shadow-md transition-shadow overflow-hidden"
            >
              <div className="p-5">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  {author.avatar ? (
                    <img
                      src={author.avatar}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover flex-shrink-0 border-2 border-slate-100 dark:border-slate-700"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center flex-shrink-0 text-lg font-bold text-slate-600 dark:text-slate-300">
                      {author.name?.charAt(0) ?? '?'}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {author.name}
                    </h4>
                    {author.position && (
                      <p className="text-xs text-slate-500 truncate">{author.position}</p>
                    )}
                    <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{author.email}</span>
                    </div>
                  </div>
                </div>

                {author.bio && (
                  <p className="text-xs text-slate-500 mt-3 line-clamp-2">{author.bio}</p>
                )}

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <span className="text-[10px] text-slate-400">
                    Joined {author.createdAt ? formatRelativeTime(author.createdAt) : '—'}
                  </span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    <button
                      onClick={() => { setEditingAuthor(author); setEditorOpen(true); }}
                      className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="Edit"
                      aria-label={`Edit ${author.name}`}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => { setDeletingAuthor(author); setDeleteDialogOpen(true); }}
                      className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Delete"
                      aria-label={`Delete ${author.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Author Editor Dialog */}
      <AuthorEditor
        isOpen={editorOpen}
        author={editingAuthor}
        onClose={() => { setEditorOpen(false); setEditingAuthor(null); }}
        onSave={handleEditorSave}
      />

      {/* Delete Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeletingAuthor(null); }}
        onConfirm={handleDeleteConfirm}
        title="Delete Author"
        itemName={deletingAuthor?.name ?? ''}
        itemType="Author"
        warnings={['Blog posts by this author may be affected.']}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AuthorList;
